/**
 * Borr Visualizer overlay.
 *
 * blocks/borr-try-in-room.liquid renders a <borr-try-in-room> element carrying the deep link for the
 * product it sits on. Pressing it opens the visualizer in a full-screen overlay on top of the shop, so
 * the visitor never leaves the product page: back/close returns them to the exact spot they were at,
 * with the cart, the wishlist and the rest of the session untouched.
 *
 * The overlay itself is built once, lazily, and appended to <body>. It is NOT rendered by Liquid on
 * purpose — several blocks can be on one page (main product, featured product, quick view) and each
 * would otherwise ship its own copy of the same markup.
 *
 * The iframe is created on open and destroyed on close: a WebGL context left running in a hidden frame
 * keeps a phone's GPU awake for the whole browsing session. Reopening is cheap anyway — the visualizer
 * ships its own service worker, so the second open comes off the local cache.
 */
(() => {
  if (window.__borrVisualizer) return;

  const READY_TIMEOUT_MS = 20000; // the veil never outlives this, however the frame ends up behaving
  const MSG_SOURCE = 'borr-visualizer';

  const state = {
    root: null,
    frame: null,
    veil: null,
    full: null,
    fullLabel: '',
    fullExitLabel: '',
    opener: null, // the button that opened it, so focus can go back where it came from
    scrollY: 0,
    timer: null,
    open: false,
    pushedHistory: false,
  };

  const svgClose =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const svgExpand =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 4H4v5M15 4h5v5M15 20h5v-5M9 20H4v-5"/></svg>';
  const svgCollapse =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9h5V4M20 9h-5V4M20 15h-5v5M4 15h5v5"/></svg>';

  function build() {
    const root = document.createElement('div');
    root.className = 'borr-viz';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.hidden = true;
    // No title row any more: the overlay opens FROM the product page, so repeating "Borr Visualizer"
    // and the full product name above the scene only stole height from the room itself.
    root.innerHTML = `
      <div class="borr-viz__panel">
        <div class="borr-viz__head">
          <button class="borr-viz__full" type="button">${svgExpand}<span></span></button>
          <button class="borr-viz__close" type="button">${svgClose}<span></span></button>
        </div>
        <div class="borr-viz__stage">
          <div class="borr-viz__veil">
            <div class="borr-viz__spinner"></div>
            <p></p>
          </div>
        </div>
      </div>`;
    document.body.appendChild(root);

    state.root = root;
    state.veil = root.querySelector('.borr-viz__veil');
    state.full = root.querySelector('.borr-viz__full');

    // a click on the backdrop (never on the panel) closes, same as any drawer in this theme
    root.addEventListener('click', (e) => {
      if (e.target === root) close();
    });
    root.querySelector('.borr-viz__close').addEventListener('click', () => close());
    state.full.addEventListener('click', () => toggleFullscreen());
    // The browser can leave fullscreen without us (Esc, the system gesture), so the button's label and
    // icon follow the document's actual state rather than a flag we set on click.
    document.addEventListener('fullscreenchange', syncFullscreenButton);
    document.addEventListener('webkitfullscreenchange', syncFullscreenButton);
    return root;
  }

  function isFullscreen() {
    return !!(document.fullscreenElement || document.webkitFullscreenElement);
  }

  function syncFullscreenButton() {
    if (!state.full) return;
    const on = isFullscreen();
    state.full.querySelector('svg').outerHTML = on ? svgCollapse : svgExpand;
    state.full.querySelector('span').textContent = on
      ? state.fullExitLabel || 'Küçült'
      : state.fullLabel || 'Tam ekran';
    state.full.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  // Replaces the old "open in a new tab" link. Leaving borr.com.tr was exactly what the embed exists to
  // avoid; fullscreen gives the scene the whole screen without ever navigating away. iOS Safari on iPhone
  // has no Element.requestFullscreen, so there the panel just expands to fill the viewport via a class.
  function toggleFullscreen() {
    const panel = state.root.querySelector('.borr-viz__panel');
    if (isFullscreen()) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      return;
    }
    const req = panel.requestFullscreen || panel.webkitRequestFullscreen;
    if (req) {
      const result = req.call(panel);
      if (result && result.catch) result.catch(() => state.root.classList.toggle('is-maxi'));
    } else {
      state.root.classList.toggle('is-maxi');
      state.full.querySelector('svg').outerHTML = state.root.classList.contains('is-maxi') ? svgCollapse : svgExpand;
      state.full.querySelector('span').textContent = state.root.classList.contains('is-maxi')
        ? state.fullExitLabel || 'Küçült' : state.fullLabel || 'Tam ekran';
    }
  }

  function lockScroll() {
    state.scrollY = window.scrollY || 0;
    document.documentElement.classList.add('borr-viz-locked');
    // iOS ignores overflow:hidden on the scrolling element, so pin the body as well and put the page
    // back exactly where it was on close
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  function unlockScroll() {
    document.documentElement.classList.remove('borr-viz-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
  }

  function restoreScroll() {
    window.scrollTo(0, state.scrollY);
  }

  function open(host) {
    if (state.open) return;
    const url = host.dataset.url;
    if (!url) return;
    try { if (new URL(url).origin !== 'https://borr.luum.tech') return; } catch (_) { return; }

    const root = state.root || build();
    state.opener = host.querySelector('.borr-try__btn') || host;
    root.setAttribute('aria-label', host.dataset.title || 'Borr Visualizer');
    root.querySelector('.borr-viz__close span').textContent = host.dataset.closeLabel || 'Kapat';
    state.fullLabel = host.dataset.fullLabel || 'Tam ekran';
    state.fullExitLabel = host.dataset.fullExitLabel || 'Küçült';
    syncFullscreenButton();
    state.veil.querySelector('p').textContent = host.dataset.loadingLabel || 'Oda hazırlanıyor';
    state.veil.classList.remove('is-done');

    const frame = document.createElement('iframe');
    frame.className = 'borr-viz__frame';
    frame.src = url;
    frame.title = host.dataset.title || 'Borr Visualizer';
    // web-share and clipboard-write MUST be granted here. A cross-origin iframe has no access to
    // navigator.share/clipboard unless the embedder delegates them, and without that the app's "Paylaş"
    // silently degraded to downloading a PNG — the reported bug.
    // The legacy allowfullscreen attribute is deliberately NOT set alongside this: "fullscreen" in the
    // allow list already covers it, and setting both makes Chrome log "Allow attribute will take
    // precedence over 'allowfullscreen'" on every open.
    frame.allow = 'fullscreen; web-share; clipboard-write; accelerometer; gyroscope; xr-spatial-tracking';
    state.frame = frame;
    root.querySelector('.borr-viz__stage').insertBefore(frame, state.veil);

    root.hidden = false;
    lockScroll();
    // hidden -> displayed has to paint before the class that fades it in, or there is no transition
    requestAnimationFrame(() => root.classList.add('is-open'));
    state.open = true;

    // The visualizer posts "ready" when its own loader has faded. If it never does, the veil still has
    // to go — a spinner that outlives the thing it is covering reads as broken.
    clearTimeout(state.timer);
    state.timer = setTimeout(() => state.veil.classList.add('is-done'), READY_TIMEOUT_MS);

    // Android's back gesture should close the overlay instead of leaving the product page
    try {
      history.pushState({ borrViz: true }, '');
      state.pushedHistory = true;
    } catch (_) {
      state.pushedHistory = false;
    }

    requestAnimationFrame(() => root.querySelector('.borr-viz__close').focus());

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'borr_visualizer_open', borr_product: host.dataset.handle || '' });
    } catch (_) {}
  }

  function close({ fromPopstate = false } = {}) {
    if (!state.open) return;
    state.open = false;
    clearTimeout(state.timer);
    state.root.classList.remove('is-open');
    unlockScroll();
    // the pushed entry has to come back off, or the visitor's next Back press only undoes the overlay
    const undoHistory = state.pushedHistory && !fromPopstate;
    state.pushedHistory = false;
    if (undoHistory) {
      // history.back() lands asynchronously and the browser restores THAT entry's remembered scroll
      // position on the way — which on a long product page is not where the visitor was standing. Put the
      // real position back once it has landed (the timeout covers a back that never fires popstate).
      addEventListener('popstate', () => requestAnimationFrame(restoreScroll), { once: true });
      setTimeout(restoreScroll, 250);
      try { history.back(); } catch (_) { restoreScroll(); }
    } else {
      restoreScroll();
    }
    // drop the frame only after the fade, so the panel does not go black on the way out
    const frame = state.frame;
    state.frame = null;
    setTimeout(() => {
      if (frame) frame.remove();
      if (!state.open) state.root.hidden = true;
    }, 300);
    if (state.opener && typeof state.opener.focus === 'function') state.opener.focus();
  }

  // Escape pressed on the shop side. Escape pressed INSIDE the frame can't be seen from here, which is
  // why the visualizer forwards it as a "close" message (see below).
  addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.open) close();
  });

  addEventListener('popstate', () => {
    if (state.open) close({ fromPopstate: true });
  });

  addEventListener('message', (e) => {
    const data = e.data;
    if (e.origin !== 'https://borr.luum.tech') return;
    if (!data || data.source !== MSG_SOURCE) return;
    if (!state.frame || e.source !== state.frame.contentWindow) return;   // only the frame we opened
    if (data.type === 'ready') {
      clearTimeout(state.timer);
      state.veil.classList.add('is-done');
    } else if (data.type === 'close') {
      close();
    }
  });

  class BorrTryInRoom extends HTMLElement {
    connectedCallback() {
      if (this.__wired) return;
      this.__wired = true;
      const btn = this.querySelector('.borr-try__btn');
      if (btn) btn.addEventListener('click', () => open(this));
    }
  }

  if (!customElements.get('borr-try-in-room')) {
    customElements.define('borr-try-in-room', BorrTryInRoom);
  }

  window.__borrVisualizer = { open, close };
})();
